import { useEffect, useMemo, useState, useCallback, useRef, type MouseEvent as ReactMouseEvent } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building2, ChevronDown, Menu as MenuIcon } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useFavorites } from '../../hooks/useFavorites';
import { useSubscription } from '../../hooks/useSubscription';
import { useMessages } from '../../hooks/useMessages';
import { useCompany } from '../../hooks/useCompany';
import CompanyBadge from '../common/CompanyBadge';
import CoinBalanceBadge from '../coins/CoinBalanceBadge';
import SafaqatLogo from '../brand/SafaqatLogo';
import Sidebar from './nav/Sidebar';
import RegisterMenu from './nav/RegisterMenu';
import LanguageToggle from './nav/LanguageToggle';
import { useListPropertyAction } from './nav/useListPropertyAction';
import {
  BUY_ITEMS,
  FIND_PRO_ITEMS,
  INVEST_ITEMS,
  MARKET_TOOLS_ITEM,
  NAV_CARD_GRID_ENABLED,
  NON_SAUDI_ITEM,
  POST_REQUEST_ITEM,
  PRO_ROLES_ENABLED,
  RENT_ITEMS,
  SUBSCRIPTIONS_ITEM,
  accountBadge,
  canSeeSubscriptions,
  isAdvertiserAccount,
  signInState,
  userDisplayName,
  type NavItem,
} from './nav/navItems';

/**
 * The site header (owner, 2026-10). One row, in reading order:
 *
 *   logo | ☰  للإيجار · للبيع · للاستثمار · تملّك لغير السعوديين · ابحث عن · خدماتنا   …   account · language
 *
 * - The hamburger sits right after the logo's divider at EVERY width and opens
 *   the sidebar (nav/Sidebar.tsx), which carries everything, including the
 *   appearance switch. The menu row shows from xl; below it the sidebar is the
 *   navigation.
 * - Guests see a plain «تسجيل الدخول» link (from sm) and the green «سجل معنا»
 *   menu (nav/RegisterMenu.tsx). Signed-in users see neither: the bell, the
 *   coin balance (2xl) and their account menu (xl) instead.
 * - Destinations live in nav/navItems.ts; the copy in i18n `nav` + `common`.
 *
 * Width budget (measured with the real fonts): the one-row bar must fit at
 * 1280px in English, its longest language. That is why the items are px-2
 * below 2xl, the language switch is a single button and the appearance switch
 * moved into the sidebar. The user's name, company chip and coin balance add
 * ~270px, so they appear from 1920px; item icons from 2xl.
 */
/**
 * "Skip to content": focus the page's own <main> (most carry id="main-content"),
 * else the router's #page-content wrapper, so the next Tab lands past the nav.
 * The wrapper wraps the nav too, so it is only the last resort.
 */
function skipToMainContent(event: ReactMouseEvent<HTMLAnchorElement>) {
  const target =
    document.getElementById('main-content') ??
    document.querySelector<HTMLElement>('main') ??
    document.getElementById('page-content');
  if (!target) return;
  event.preventDefault();
  if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
  target.focus();
}

export default function Navigation() {
  const { user, signOut } = useAuth();
  const { isSubscribed } = useSubscription();
  const { unreadCount } = useMessages();
  const { company } = useCompany();
  const { lists } = useFavorites();
  const { t, i18n } = useTranslation(['common', 'nav']);
  const navigate = useNavigate();
  const location = useLocation();
  const listProperty = useListPropertyAction();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  // Which desktop browse dropdown is open via keyboard/click (hover is handled
  // by CSS independently). `null` = none open.
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  // Tracks which dropdown currently holds keyboard focus, so aria-expanded stays
  // accurate when a panel opens via :focus-within (read-only — it never drives
  // openDropdown, so it can't fight the click-to-toggle on the trigger button).
  const [focusWithinKey, setFocusWithinKey] = useState<string | null>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  const isRTL = i18n.dir() === 'rtl';

  // A language switch re-lays the bar out, so close any open menu. The sidebar
  // stays open: its own language row is what changed the language.
  useEffect(() => {
    setShowUserMenu(false);
    setOpenDropdown(null);
  }, [i18n.resolvedLanguage]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (showUserMenu && userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  // Close the open browse dropdown when clicking outside any trigger.
  useEffect(() => {
    if (!openDropdown) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.nav-dropdown-trigger')) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  // Toggle a desktop browse dropdown via click/keyboard. Hover remains a CSS-only
  // progressive enhancement; this state path is what makes them keyboard-operable.
  const toggleDropdown = useCallback((key: string) => {
    setOpenDropdown((prev) => (prev === key ? null : key));
  }, []);

  const handleDropdownKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, key: string) => {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        toggleDropdown(key);
      } else if (e.key === 'Escape') {
        setOpenDropdown((prev) => (prev === key ? null : prev));
        // Return focus to the trigger so keyboard users aren't stranded.
        e.currentTarget.focus();
      }
    },
    [toggleDropdown]
  );

  const handleSignOut = async () => {
    try {
      await signOut();
      setShowUserMenu(false);
      setIsSidebarOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const closeSidebar = useCallback(() => setIsSidebarOpen(false), []);

  const displayName = useMemo(() => userDisplayName(user) || t('auth.signIn'), [user, t]);

  const companyBadge = user?.companyBadge as { companyId: string; companyName: string; companyNameAr: string; companyLogoUrl: string; teamRole: 'OWNER' | 'ADMIN' | 'AGENT' | 'VIEWER' } | null | undefined;
  const accountType = user?.accountType;
  const isPropertyOwner = isAdvertiserAccount(user);

  // Compact tag shown next to the user's name in the top nav.
  // - Company member: "CompanyName" (+ " (Broker)" when the team role is AGENT
  //   or the account is a broker). The detailed team-role pill still appears
  //   inside the dropdown via <CompanyBadge>.
  // - Independent broker (no company): "Broker".
  // - Plain individual / not-enrolled user: no tag at all. Showing "IND" on
  //   every visitor was noise and misleading on company admins.
  const chipInfo = useMemo<{ text: string; tone: 'company' | 'broker' } | null>(() => {
    const cbName = companyBadge
      ? (isRTL ? companyBadge.companyNameAr || companyBadge.companyName : companyBadge.companyName)
      : null;
    if (cbName) {
      const isBrokerInCompany =
        companyBadge?.teamRole === 'AGENT' || accountType === 'broker';
      if (isBrokerInCompany) {
        return {
          text: `${cbName} (${t('nav:badges.broker')})`,
          tone: 'broker',
        };
      }
      return { text: cbName, tone: 'company' };
    }
    if (accountType === 'broker') {
      return { text: t('nav:badges.broker'), tone: 'broker' };
    }
    if (accountType === 'company') {
      // Edge case: registered as company but no companyBadge attached yet.
      return { text: t('nav:badges.company'), tone: 'company' };
    }
    return null;
  }, [companyBadge, accountType, isRTL, t]);

  // The account-type pill in the user menu header. Team members get the
  // CompanyBadge instead; plain individuals get nothing.
  const menuBadge = accountBadge(user);

  // Locked, solid top bar (2026-09). Pinned to the top of the viewport at all
  // times — no hide-on-scroll, no glass/transparency, no state-dependent colours:
  // the same opaque white (deep green in dark mode) whether at the top of the
  // page or scrolled. The sticky <nav> reserves its own layout space so page
  // content never slides underneath it.
  const navClassName = 'sticky top-0 z-50 w-full';

  const navPillClass = [
    'relative flex w-full flex-col',
    'bg-white dark:bg-[#041C18]',
    'border-b border-[var(--pv-border-soft)] dark:border-white/10',
    // No shadow. The page is white; a single hairline is the whole separation.
  ].join(' ');

  // Calm, sharp nav item — sentence case (no uppercase/letter-spacing), soft
  // brand-tinted hover pill, smooth colour transition. px-2 below 2xl keeps the
  // English row inside 1280px.
  const navItemClass =
    'flex items-center gap-1 whitespace-nowrap rounded-md px-2 py-2 text-[15px] font-medium text-[var(--pv-color-text)] dark:text-[var(--pv-color-text-muted)] hover:text-[var(--pv-color-primary)] hover:bg-[rgba(0,77,64,0.06)] transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)]/60 2xl:gap-1.5 2xl:px-2.5';

  // State-driven visibility for the desktop browse dropdowns. Inline styles win
  // over the stylesheet's `opacity:0` default, so keyboard/click opening works
  // independently of the CSS :hover/:focus-within rules (which still apply when
  // these styles are absent).
  const dropdownPanelStyle = (key: string): React.CSSProperties =>
    openDropdown === key ? { opacity: 1, pointerEvents: 'auto', visibility: 'visible' } : {};
  const dropdownInnerStyle = (key: string): React.CSSProperties =>
    openDropdown === key ? { opacity: 1, transform: 'translateY(0)' } : {};

  // Shared dropdown menu item style
  const dropdownItemClass = 'nav-dropdown-item';

  // Wrapper-level handlers so a dropdown's open state tracks keyboard focus
  // (keeps aria-expanded accurate when Tab opens it via :focus-within) and
  // Escape closes it from anywhere inside the panel, returning focus to the
  // trigger. Mouse hover stays a CSS-only enhancement.
  const dropdownTriggerProps = (key: string) => ({
    className: 'nav-dropdown-trigger',
    // Mark this dropdown as keyboard-focused (drives aria-expanded only).
    onFocus: () => setFocusWithinKey(key),
    // When focus leaves the whole trigger+panel, clear the focus flag and close
    // a state-opened panel so Tab-out doesn't leave it stuck open.
    onBlur: (e: React.FocusEvent<HTMLDivElement>) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
        setFocusWithinKey((prev) => (prev === key ? null : prev));
        setOpenDropdown((prev) => (prev === key ? null : prev));
      }
    },
    // Escape closes the panel and returns focus to the trigger button.
    onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        setOpenDropdown((prev) => (prev === key ? null : prev));
        e.currentTarget.querySelector('button')?.focus();
      }
    },
  });

  const itemTitle = (item: NavItem) => t(item.labelKey);
  const itemDesc = (item: NavItem) => (item.descKey ? t(item.descKey) : undefined);

  // Inner markup for a card-grid menu item (icon tile + title + optional subtitle).
  const megaItemInner = (icon: string, title?: string, desc?: string) => (
    <>
      <span className="nav-mega-ico"><i className={icon} aria-hidden="true"></i></span>
      <span className="nav-mega-body">
        <span className="nav-mega-title">{title}</span>
        {desc ? <span className="nav-mega-desc">{desc}</span> : null}
      </span>
    </>
  );
  // Inner markup for a plain list menu item.
  const plainItemInner = (icon: string, title?: string) => (
    <>
      <i className={icon} aria-hidden="true"></i>
      <span>{title}</span>
    </>
  );

  const triggerIconClass = (icon: string) =>
    `${icon} hidden text-xl leading-none text-[var(--pv-color-primary)] 2xl:inline`;

  // Plain browse dropdown (the pre-redesign list) — kept as the OFF fallback
  // for the NAV_CARD_GRID flag.
  const renderBrowseDropdown = (
    key: string,
    label: string,
    triggerIcon: string,
    items: readonly NavItem[],
  ) => (
    <div key={key} {...dropdownTriggerProps(key)}>
      <button
        type="button"
        className={`${navItemClass} ${openDropdown === key ? 'is-open' : ''}`}
        aria-haspopup="true"
        aria-expanded={openDropdown === key || focusWithinKey === key}
        onClick={() => toggleDropdown(key)}
        onKeyDown={(e) => handleDropdownKeyDown(e, key)}
      >
        <i className={triggerIconClass(triggerIcon)} aria-hidden="true"></i>
        {label}
        <ChevronDown className="nav-chevron h-3.5 w-3.5 opacity-60" aria-hidden="true" />
      </button>
      <div className="nav-dropdown-panel" style={dropdownPanelStyle(key)}>
        <div className="nav-dropdown-panel-inner" style={dropdownInnerStyle(key)}>
          {items.map((item) => (
            <Link key={item.to} to={item.to} onClick={() => setOpenDropdown(null)} className={dropdownItemClass}>
              {plainItemInner(item.icon, itemTitle(item))}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );

  // Card-grid browse dropdown (the signature 2-col mega-panel). Reuses the exact
  // .nav-mega* classes the "Search for…" menu introduced, so every dropdown
  // shares one look.
  const renderMegaDropdown = (
    key: string,
    label: string,
    triggerIcon: string,
    items: readonly NavItem[],
  ) => (
    <div key={key} {...dropdownTriggerProps(key)}>
      <button
        type="button"
        className={`${navItemClass} ${openDropdown === key ? 'is-open' : ''}`}
        aria-haspopup="true"
        aria-expanded={openDropdown === key || focusWithinKey === key}
        onClick={() => toggleDropdown(key)}
        onKeyDown={(e) => handleDropdownKeyDown(e, key)}
      >
        <i className={triggerIconClass(triggerIcon)} aria-hidden="true"></i>
        {label}
        <ChevronDown className="nav-chevron h-3.5 w-3.5 opacity-60" aria-hidden="true" />
      </button>
      <div className="nav-dropdown-panel" style={dropdownPanelStyle(key)}>
        <div className="nav-dropdown-panel-inner nav-mega" style={dropdownInnerStyle(key)}>
          <div className="nav-mega-grid">
            {items.map((item) => (
              <Link key={item.to} to={item.to} onClick={() => setOpenDropdown(null)} className="nav-mega-item">
                {megaItemInner(item.icon, itemTitle(item), itemDesc(item))}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // Flag-driven selector: card grid when on, plain list otherwise.
  const renderDropdown = NAV_CARD_GRID_ENABLED ? renderMegaDropdown : renderBrowseDropdown;

  // The Services menu: four entries only (owner, 2026-10). Everything else in
  // the old menu (professional services, the Requests board, add-ons, bundles,
  // coins, my add-ons) lives in the sidebar's «خدماتنا» card.
  type ServiceEntry = {
    key: string;
    icon: string;
    title: string;
    desc?: string;
    to: string;
    state?: unknown;
    onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  };
  const fromItem = (item: NavItem): ServiceEntry => ({
    key: item.to,
    icon: item.icon,
    title: itemTitle(item),
    desc: itemDesc(item),
    to: item.to,
  });
  const servicesEntries: ServiceEntry[] = [
    {
      key: 'list',
      icon: 'ri-add-circle-line',
      title: t('navigation.listProperty'),
      desc: t('nav:services.listPropertyDesc'),
      to: listProperty.to,
      state: listProperty.state,
      onClick: (e) => listProperty.onClick(e),
    },
    fromItem(POST_REQUEST_ITEM),
    ...(canSeeSubscriptions(user) ? [fromItem(SUBSCRIPTIONS_ITEM)] : []),
    fromItem(MARKET_TOOLS_ITEM),
  ];
  const renderServiceEntry = (e: ServiceEntry) => (
    <Link
      key={e.key}
      to={e.to}
      state={e.state}
      onClick={(ev) => { e.onClick?.(ev); setOpenDropdown(null); }}
      className={NAV_CARD_GRID_ENABLED ? 'nav-mega-item' : dropdownItemClass}
    >
      {NAV_CARD_GRID_ENABLED ? megaItemInner(e.icon, e.title, e.desc) : plainItemInner(e.icon, e.title)}
    </Link>
  );

  // Pill for the account chip and badges: text-xs at the smallest (proportion rule).
  const pillClass = 'rounded-full px-2 py-0.5 text-xs font-semibold leading-none';

  return (
    <>
      {/* Skip link — first focusable element so keyboard/screen-reader users can
          jump past the nav. Targets #main-content (page main containers should
          carry that id); skipToMainContent falls back to any <main>. */}
      <a
        href="#main-content"
        onClick={skipToMainContent}
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:start-2 focus:z-[100] focus:rounded-lg focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-[var(--pv-color-primary)] focus:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)]/60"
      >
        {t('nav:skipToContent')}
      </a>
      {/* Named: the sidebar carries a second <nav> landmark («القائمة»). */}
      <nav className={navClassName} dir={i18n.dir()} aria-label={t('navigation.sidebar.navigation')}>
       <div className={navPillClass}>
        <div className="relative z-10 flex h-[76px] w-full items-center gap-2 px-5 sm:px-8 lg:px-10">
          {/* Logo — leads the bar at the inline-start edge (the right side in
              Arabic), the same edge the footer logo sits on. Static: no
              hover/tap scaling, so it stays fixed in place. */}
          <div className="relative z-10 shrink-0">
            <Link to="/" className="flex items-center">
              {/* The designer's artwork, served as PNG by SafaqatLogo; it
                  swaps to the white-outline variant under `.dark`. */}
              <SafaqatLogo className="site-logo h-[60px] w-auto" title={t('brand.name')} />
            </Link>
          </div>

          {/* The divider between the logo and the menu: a 1px hairline, the
              same brand-teal-at-16% as every other rule on the page. */}
          <span className="mx-2 h-8 w-px shrink-0 bg-[var(--pv-border-soft)] dark:bg-white/15" aria-hidden="true" />

          {/* The one menu button, right after the divider at every width (R9).
              It opens the sidebar, which carries the whole site menu. */}
          <button
            ref={menuButtonRef}
            type="button"
            data-sidebar-toggle
            onClick={() => setIsSidebarOpen(true)}
            aria-label={t('nav:menu.open')}
            aria-haspopup="dialog"
            aria-expanded={isSidebarOpen}
            aria-controls="sidebar"
            className="grid h-11 w-11 shrink-0 place-items-center rounded-xl text-[var(--pv-color-text)] transition-colors hover:bg-[rgba(0,77,64,0.06)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--pv-color-primary)]"
          >
            <MenuIcon className="h-[1.375rem] w-[1.375rem]" strokeWidth={2} aria-hidden="true" />
          </button>

          {/* Desktop nav (xl+) — ONE row (owner, 2026-10): the browse verbs,
              then the secondary destinations that used to sit in a second
              utility row. Below xl the sidebar carries everything. Item icons
              only appear from 2xl, so the row still fits at 1280px. */}
          <div className="hidden min-w-0 flex-1 items-center gap-0.5 xl:flex">
            {/* For Rent · For Sale · For Investment */}
            {renderDropdown('rent', t('nav:top.rent'), 'ri-key-2-line', RENT_ITEMS)}
            {renderDropdown('buy', t('nav:top.buy'), 'ri-shopping-bag-3-line', BUY_ITEMS)}
            {renderDropdown('invest', t('nav:top.invest'), 'ri-line-chart-line', INVEST_ITEMS)}

            {/* House of Deals (دار الصفقات) is hidden from the frontend for now
                (owner request). The subscriber "Special deals / صفقات خاصة" page
                stays reachable from the For Investment menu. */}

            {/* Non-Saudi Ownership */}
            <NavLink
              to={NON_SAUDI_ITEM.to}
              className={({ isActive }) => `${navItemClass} ${isActive ? 'is-open' : ''}`}
            >
              <i className={triggerIconClass(NON_SAUDI_ITEM.icon)} aria-hidden="true"></i>
              {t(NON_SAUDI_ITEM.labelKey)}
            </NavLink>

            {/* Find a professional — the directory mega-menu. */}
            <div {...dropdownTriggerProps('searchfor')} className="nav-dropdown-trigger nav-dropdown-trigger--end">
              <button
                type="button"
                className={`${navItemClass} ${openDropdown === 'searchfor' ? 'is-open' : ''}`}
                aria-haspopup="true"
                aria-expanded={openDropdown === 'searchfor' || focusWithinKey === 'searchfor'}
                onClick={() => toggleDropdown('searchfor')}
                onKeyDown={(e) => handleDropdownKeyDown(e, 'searchfor')}
              >
                <i className={triggerIconClass('ri-user-search-line')} aria-hidden="true"></i>
                {t('nav:top.findPro')}
                <ChevronDown className="nav-chevron h-3.5 w-3.5 opacity-60" aria-hidden="true" />
              </button>
              <div className="nav-dropdown-panel" style={dropdownPanelStyle('searchfor')}>
                <div className="nav-dropdown-panel-inner nav-mega" style={dropdownInnerStyle('searchfor')}>
                  <div className="nav-mega-grid">
                    {FIND_PRO_ITEMS.map((item) => (
                      <Link key={item.to} to={item.to} onClick={() => setOpenDropdown(null)} className="nav-mega-item">
                        {megaItemInner(item.icon, itemTitle(item), itemDesc(item))}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Services: list a property, post a request, plans, market intelligence. */}
            <div {...dropdownTriggerProps('services')} className="nav-dropdown-trigger nav-dropdown-trigger--end">
              <button
                type="button"
                className={`${navItemClass} ${openDropdown === 'services' ? 'is-open' : ''}`}
                aria-haspopup="true"
                aria-expanded={openDropdown === 'services' || focusWithinKey === 'services'}
                onClick={() => toggleDropdown('services')}
                onKeyDown={(e) => handleDropdownKeyDown(e, 'services')}
              >
                <i className={triggerIconClass('ri-apps-2-line')} aria-hidden="true"></i>
                {t('nav:top.services')}
                <ChevronDown className="nav-chevron h-3.5 w-3.5 opacity-60" aria-hidden="true" />
              </button>
              <div className="nav-dropdown-panel" style={dropdownPanelStyle('services')}>
                <div className={`nav-dropdown-panel-inner ${NAV_CARD_GRID_ENABLED ? 'nav-mega' : ''}`} style={dropdownInnerStyle('services')}>
                  <div className={NAV_CARD_GRID_ENABLED ? 'nav-mega-grid' : ''}>
                    {servicesEntries.map(renderServiceEntry)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Account and language — one cluster at every width, at the inline end. */}
          <div className="ms-auto flex shrink-0 items-center gap-1">
            {/* Notification Bell */}
            {user && isSubscribed && (
              <Link
                to="/enquiries"
                className="relative grid h-11 w-11 place-items-center rounded-full text-[var(--pv-color-text-muted)] transition-colors hover:text-[var(--pv-color-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)]/60"
                title={t('navigation.enquiries')}
                aria-label={unreadCount > 0 ? t('nav:enquiriesUnread', { n: unreadCount }) : t('navigation.enquiries')}
              >
                <i aria-hidden="true" className="ri-notification-3-line text-[1.375rem] leading-none" />
                {unreadCount > 0 && (
                  <span
                    aria-hidden="true"
                    className="absolute top-0.5 end-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#DF2F2B] px-1 text-xs font-bold leading-none text-white"
                  >
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Link>
            )}

            {/* Coin balance pill — self-hides when signed out / no balance.
                From 1920px only, like the name and chip below: shown from 2xl
                they pushed a signed-in bar 50-260px past the screen at
                1536-1600px (measured). Coins stay in the sidebar. */}
            {user && <div className="hidden min-[1920px]:block"><CoinBalanceBadge /></div>}

            {user ? (
              <div className="relative hidden xl:block" ref={userMenuRef}>
                <button
                  type="button"
                  onClick={() => setShowUserMenu((prev) => !prev)}
                  aria-haspopup="menu"
                  aria-expanded={showUserMenu}
                  aria-controls="user-menu"
                  aria-label={displayName}
                  className="flex items-center gap-2 rounded-md px-2 py-2 text-sm text-[var(--pv-color-text)] transition-colors hover:bg-[rgba(0,77,64,0.06)] min-[1920px]:gap-3 min-[1920px]:px-4"
                >
                  <span className="grid h-10 w-10 place-items-center rounded-full bg-[var(--pv-color-surface-sunken)] ring-1 ring-[#DDEEEB] text-[1.25rem] text-[var(--pv-color-primary)]">
                    <i className="ri-user-line" aria-hidden="true"></i>
                  </span>
                  <span className={`hidden items-center gap-1.5 min-w-0 min-[1920px]:flex ${isRTL ? 'order-first' : ''}`}>
                    <span className="truncate">{displayName}</span>
                    {chipInfo && (
                      <span
                        className={`${pillClass} truncate max-w-[160px] ${
                          chipInfo.tone === 'company'
                            ? 'bg-[#DDEEEB] text-[#004D40]'
                            : 'bg-slate-100 text-slate-700'
                        }`}
                        title={chipInfo.text}
                      >
                        {chipInfo.text}
                      </span>
                    )}
                  </span>
                  <i aria-hidden="true" className={[
                    'ri-arrow-down-s-line text-lg text-[rgba(31,26,58,0.45)] transition-transform duration-200',
                    showUserMenu ? 'rotate-180' : '',
                  ].join(' ')}></i>
                </button>

                {/* User Dropdown Menu */}
                <div
                  id="user-menu"
                  role="menu"
                  aria-label={t('nav:account.menu')}
                  className={[
                    'absolute mt-3 w-72 max-h-[calc(100vh-6rem)] overflow-y-auto rounded-2xl border border-[var(--pv-border-soft)] dark:border-white/10 bg-[var(--pv-color-surface-strong)] dark:bg-[#062B25] text-sm text-[var(--pv-color-text)] shadow-[0_2px_4px_rgba(0,77,64,0.06),0_16px_36px_-14px_rgba(0,77,64,0.28)]',
                    isRTL ? 'left-0' : 'right-0',
                    showUserMenu ? 'visible' : 'invisible pointer-events-none',
                  ].join(' ')}
                >
                  {/* User Header */}
                  <div className="px-4 pt-4 pb-3 border-b border-[var(--pv-border-soft)]">
                    <p className="font-semibold text-[var(--pv-color-text)]">{displayName}</p>
                    <p className="text-xs text-[var(--pv-color-text-muted)] mt-0.5"><bdi>{user.email}</bdi></p>
                    {/* Team members get the CompanyBadge below instead (company
                        logo + team role, more informative). Plain individuals
                        need no label; an individual who listed under Safaqat
                        24's FAL licence gets that badge. */}
                    {menuBadge && !(companyBadge && menuBadge.labelKey !== 'common:badges.falIndividual') && (
                      <span className={`inline-flex items-center gap-1 mt-1.5 ${pillClass} ${
                        menuBadge.tone === 'broker'
                          ? 'bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-slate-200'
                          : 'bg-[#DDEEEB] text-[#004D40] dark:bg-[#0A4A3E] dark:text-[#7FD6C3]'
                      }`}>
                        {menuBadge.labelKey === 'common:badges.falIndividual' && (
                          <i className="ri-verified-badge-fill" aria-hidden="true"></i>
                        )}
                        {t(menuBadge.labelKey)}
                      </span>
                    )}
                    {companyBadge && (
                      <div className="mt-1">
                        <CompanyBadge
                          teamRole={companyBadge.teamRole}
                          companyName={isRTL ? companyBadge.companyNameAr : companyBadge.companyName}
                          size="sm"
                        />
                      </div>
                    )}
                  </div>

                  <div className="p-2">
                    {/* Profile */}
                    <Link to="/profile" onClick={() => setShowUserMenu(false)} className={dropdownItemClass} role="menuitem">
                      <i className="ri-user-line" aria-hidden="true"></i>
                      <span>{t('navigation.profile')}</span>
                    </Link>

                    {/* Grow your account — professional roles (flag-gated) */}
                    {PRO_ROLES_ENABLED && (
                      <Link to="/account/professional" onClick={() => setShowUserMenu(false)} className={dropdownItemClass} role="menuitem">
                        <i className="ri-briefcase-line" aria-hidden="true"></i>
                        <span>{t('nav:account.grow')}</span>
                      </Link>
                    )}

                    {/* Dashboard — independent brokers only. Company members
                        get a richer view at /company/dashboard, rendered as a
                        separate link further down. Showing both is redundant. */}
                    {isSubscribed && !company && (
                      <Link to="/dashboard" onClick={() => setShowUserMenu(false)} className={dropdownItemClass} role="menuitem">
                        <i className="ri-dashboard-line" aria-hidden="true"></i>
                        <span>{t('navigation.sidebar.dashboard')}</span>
                      </Link>
                    )}

                    {/* My Listings - subscribed or company */}
                    {(isSubscribed || company) && (
                      <Link to="/listings" onClick={() => setShowUserMenu(false)} className={dropdownItemClass} role="menuitem">
                        <i className="ri-building-line" aria-hidden="true"></i>
                        <span>{t('navigation.sidebar.myListings')}</span>
                      </Link>
                    )}

                    {/* Client Requests - brokers */}
                    {isPropertyOwner && (
                      <Link to="/broker/requests" onClick={() => setShowUserMenu(false)} className={dropdownItemClass} role="menuitem">
                        <i className="ri-file-list-2-line" aria-hidden="true"></i>
                        <span>{t('navigation.sidebar.brokerRequests')}</span>
                      </Link>
                    )}

                    {/* My Requests */}
                    <Link to="/my-requests" onClick={() => setShowUserMenu(false)} className={dropdownItemClass} role="menuitem">
                      <i className="ri-folder-user-line" aria-hidden="true"></i>
                      <span>{t('navigation.sidebar.myRequests')}</span>
                    </Link>

                    {/* Favorites */}
                    <Link to="/favorites" onClick={() => setShowUserMenu(false)} className="nav-dropdown-item justify-between" role="menuitem">
                      <div className="flex items-center gap-3">
                        <i className="ri-heart-line" aria-hidden="true"></i>
                        <span>{t('navigation.sidebar.favorites')}</span>
                      </div>
                      {lists.length > 1 && (
                        <span className="text-xs bg-[var(--pv-color-surface-sunken)] dark:bg-[rgba(0,77,64,0.15)] text-[#004D40] px-2 py-0.5 rounded-full">
                          {lists.length}
                        </span>
                      )}
                    </Link>

                    {/* Enquiries - subscribed or company */}
                    {(isSubscribed || company) && (
                      <Link to="/enquiries" onClick={() => setShowUserMenu(false)} className="nav-dropdown-item justify-between" role="menuitem">
                        <div className="flex items-center gap-3">
                          <i className="ri-notification-3-line" aria-hidden="true"></i>
                          <span>{t('navigation.enquiries')}</span>
                        </div>
                        {unreadCount > 0 && (
                          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-[#DF2F2B] px-1.5 text-xs font-bold leading-none text-white">
                            {unreadCount > 9 ? '9+' : unreadCount}
                          </span>
                        )}
                      </Link>
                    )}

                    {/* My Complaints */}
                    <Link to="/complaints/my" onClick={() => setShowUserMenu(false)} className={dropdownItemClass} role="menuitem">
                      <i className="ri-file-list-3-line" aria-hidden="true"></i>
                      <span>{t('navigation.myComplaints')}</span>
                    </Link>

                    {/* Company Dashboard */}
                    {company && (
                      <Link to="/company/dashboard" onClick={() => setShowUserMenu(false)} className={dropdownItemClass} role="menuitem">
                        <i className="ri-building-4-line" aria-hidden="true"></i>
                        <span>{t('navigation.companyDashboard')}</span>
                      </Link>
                    )}

                    {/* Team Management */}
                    {company && (
                      <Link to="/company/team" onClick={() => setShowUserMenu(false)} className={dropdownItemClass} role="menuitem">
                        <i className="ri-team-line" aria-hidden="true"></i>
                        <span>{t('navigation.teamManagement')}</span>
                      </Link>
                    )}

                    {/* Register Company */}
                    {!company && (
                      <Link to="/register?type=company" onClick={() => setShowUserMenu(false)} className={dropdownItemClass} role="menuitem">
                        <Building2 className="w-[1.125rem] h-[1.125rem] flex-shrink-0" aria-hidden="true" />
                        <span>{t('navigation.registerCompany')}</span>
                      </Link>
                    )}
                  </div>

                  {/* Settings */}
                  <div className="border-t border-[var(--pv-border-soft)] p-2">
                    <Link to="/settings" onClick={() => setShowUserMenu(false)} className={dropdownItemClass} role="menuitem">
                      <i className="ri-settings-4-line" aria-hidden="true"></i>
                      <span>{t('navigation.sidebar.accountSettings')}</span>
                    </Link>
                  </div>

                  {/* Sign Out */}
                  <div className="border-t border-[var(--pv-border-soft)] p-2">
                    <button
                      type="button"
                      onClick={handleSignOut}
                      className="nav-dropdown-item w-full text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10"
                      role="menuitem"
                    >
                      <i className="ri-logout-circle-line !text-rose-400" aria-hidden="true"></i>
                      <span>{t('auth.signOut')}</span>
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Guests (R17): a plain sign-in link beside the green «سجل معنا»
                    menu. Below sm the sidebar's guest card carries sign-in. */}
                <Link
                  to="/signin"
                  state={signInState(location)}
                  className="hidden min-h-[2.75rem] shrink-0 items-center whitespace-nowrap rounded-md px-3 text-[0.875rem] font-medium text-[var(--pv-color-text)] transition-colors duration-200 hover:bg-[rgba(0,77,64,0.06)] hover:text-[var(--pv-color-primary)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--pv-color-primary)] dark:text-[var(--pv-color-text-muted)] sm:inline-flex"
                >
                  {t('nav:signIn')}
                </Link>
                <RegisterMenu />
              </>
            )}

            {/* Language — the other language's name, one tap. The sidebar has the full switch. */}
            <LanguageToggle className="hidden sm:inline-flex" />
          </div>
        </div>

       </div>
      </nav>

      <Sidebar open={isSidebarOpen} onClose={closeSidebar} returnFocusRef={menuButtonRef} />
    </>
  );
}
